import React, { useState, useEffect } from 'react';
import { CustomFieldDefinition, CustomFieldValue, CustomFieldType } from '../../types/custom-field';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Sparkle, Trash, GearSix } from '@phosphor-icons/react';

interface CustomFieldRowProps {
  definition: CustomFieldDefinition;
  value: CustomFieldValue | undefined;
  onSave: (fieldDefId: number, value: string | number | boolean | null) => void;
  onDelete?: (fieldDefId: number) => void;
  onConfigureAI?: (fieldDefId: number) => void;
  isDisabled?: boolean;
}

const CustomFieldRow: React.FC<CustomFieldRowProps> = ({
  definition,
  value,
  onSave,
  onDelete,
  onConfigureAI,
  isDisabled = false,
}) => {
  const [localValue, setLocalValue] = useState<string | number | boolean | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (value) {
      switch (definition.fieldType) {
        case CustomFieldType.TEXT:
        case CustomFieldType.DROPDOWN:
          setLocalValue(value.textValue);
          break;
        case CustomFieldType.NUMBER:
          setLocalValue(value.numberValue);
          break;
        case CustomFieldType.BOOLEAN:
          setLocalValue(value.booleanValue);
          break;
      }
    } else {
      setLocalValue(definition.fieldType === CustomFieldType.BOOLEAN ? false : null);
    }
  }, [value, definition.fieldType]);

  const handleChange = (newValue: string | number | boolean | null) => {
    setLocalValue(newValue);
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave(definition.id, localValue);
    setIsDirty(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isDirty) {
      handleSave();
    }
  };

  const renderInput = () => {
    switch (definition.fieldType) {
      case CustomFieldType.TEXT:
        return (
          <Input
            value={(localValue as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => isDirty && handleSave()}
            disabled={isDisabled}
            placeholder={`Voer ${definition.name.toLowerCase()} in`}
            className="flex-1"
          />
        );

      case CustomFieldType.NUMBER:
        return (
          <Input
            type="number"
            value={localValue !== null ? String(localValue) : ''}
            onChange={(e) => handleChange(e.target.value ? parseFloat(e.target.value) : null)}
            onKeyDown={handleKeyDown}
            onBlur={() => isDirty && handleSave()}
            disabled={isDisabled}
            placeholder={`Voer ${definition.name.toLowerCase()} in`}
            className="flex-1"
          />
        );

      case CustomFieldType.DROPDOWN:
        return (
          <Select
            value={(localValue as string) || ''}
            onValueChange={(val) => {
              handleChange(val);
              setTimeout(() => {
                onSave(definition.id, val);
                setIsDirty(false);
              }, 0);
            }}
            disabled={isDisabled}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder={`Selecteer ${definition.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              align="start"
              className="max-h-[200px]"
              {...{ modal: false } as any}
            >
              {definition.dropdownOptions?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case CustomFieldType.BOOLEAN:
        return (
          <Switch
            checked={(localValue as boolean) || false}
            onCheckedChange={(checked) => {
              handleChange(checked);
              onSave(definition.id, checked);
              setIsDirty(false);
            }}
            disabled={isDisabled}
          />
        );

      default:
        return null;
    }
  };

  const hasAIExtraction = definition.aiExtractionConfig?.enabled;
  const isAIExtracted = value?.source === 'ai_extracted';

  return (
    <div className="flex items-center gap-3 py-2" data-tour="custom-field-row">
      <div className="flex-1 flex items-center gap-3">
        <div className="min-w-[180px]">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            {definition.name}
            {hasAIExtraction && (
              <Sparkle size={14} weight="fill" className="text-yellow-500" />
            )}
          </label>
        </div>

        <div className="flex-1 flex items-center gap-2">
          {renderInput()}
          {isAIExtracted && (
            <Badge variant="secondary" className="text-xs whitespace-nowrap">
              AI geëxtraheerd
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {onConfigureAI && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onConfigureAI(definition.id)}
            disabled={isDisabled}
            title="AI configuratie"
            className="h-8 w-8"
            data-tour="custom-field-ai-config"
          >
            <GearSix size={16} />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(definition.id)}
            disabled={isDisabled}
            title="Veld verwijderen"
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <Trash size={16} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default CustomFieldRow;
